import { useEffect, useState } from 'react';
import { supervisorApi } from '../api/supervisor.js';
import { contrastText } from '../utils/contrast.js';

let _tagCache = null;
async function loadTagDefs() {
  if (_tagCache) return _tagCache;
  try {
    const data = await supervisorApi.listTags();
    _tagCache = new Map((data?.items || []).map((t) => [t.slug, t]));
  } catch {
    _tagCache = new Map();
  }
  return _tagCache;
}

/**
 * Compact user representation: avatar + name + tag pills (max 2 visible,
 * "+N more" on hover). Used across modules anywhere a user is referenced.
 *
 * Props:
 *   user        — { id?, name, email?, avatar?, user_tags? }
 *   tags        — optional list of tag SLUGS (override; otherwise read from user.user_tags)
 *   showName    — boolean (default true)
 *   size        — 'sm' | 'md'  (default 'md')
 *   maxTags     — number of tag pills to show inline (default 2)
 */
export default function UserPill({ user, tags, showName = true, size = 'md', maxTags = 2 }) {
  const [defs, setDefs] = useState(null);
  useEffect(() => { loadTagDefs().then(setDefs); }, []);

  if (!user) return <span className="muted small">—</span>;
  const slugs = (Array.isArray(tags) ? tags : (user.user_tags || []));
  const visible = slugs.slice(0, maxTags);
  const overflow = Math.max(0, slugs.length - maxTags);

  return (
    <span className={`user-pill ${size}`}>
      <span className="up-avatar" title={user.name}>{(user.name || '?').charAt(0).toUpperCase()}</span>
      {showName && <span className="up-name">{user.name}</span>}
      {visible.map((slug) => {
        const t = defs?.get(slug);
        const color = t?.color || '#6366f1';
        return <span key={slug} className="up-tag" style={{ background: color, color: contrastText(color) }} title={t?.name || slug}>{t?.name || slug}</span>;
      })}
      {overflow > 0 && (
        <span className="up-more" title={slugs.slice(maxTags).join(', ')}>+{overflow}</span>
      )}
    </span>
  );
}

/** Small standalone tag pill */
export function TagPill({ slug, name, color }) {
  const [defs, setDefs] = useState(null);
  useEffect(() => { if (!color) loadTagDefs().then(setDefs); else setDefs(null); }, [color]);
  const def = defs?.get(slug);
  const bg = color || def?.color || '#6366f1';
  return <span className="up-tag" style={{ background: bg, color: contrastText(bg) }}>{name || def?.name || slug}</span>;
}
